import Button from '@carvana/ds-button';
import Spinner from '@carvana/ds-spinner';
import { ToastProvider } from '@carvana/ds-toast';
import { ThemeProvider } from '@carvana/theme';
import React, { useState, useCallback } from 'react';
import type { SlotPreferenceScope } from '|/hooks/use-slot-preference';
import { AddressDisplay } from './components/AddressDisplay';
import { TimeSlotSelectionAlertModal } from './components/AlertModals';
import { SchedulingErrorPrompt } from './components/SchedulingErrorPrompt';
import { TimeSlotPicker } from './components/TimeSlotPicker';
import { VinEtchingBanner } from './components/VinEtchingBanner';
import { OrderSummaryProvider } from './contexts/orderSummary';
import { TransferScheduleProvider, useTransferScheduleContext } from './contexts/transferSchedule';
import { loadTimeSlots } from './contexts/transferSchedule/transferSchedule.actions';
import styles from './transfer-select-slot.module.css';
import useTransferSelectSlotVm from './use-transfer-select-slot-vm';
import { isBlockingError } from './utils/errorHandling';
import { TIME_SLOT_SELECTION_ERROR } from './utils/submissionErrorConstants';

interface TransferSelectSlotProps {
  slotPreferenceScope?: SlotPreferenceScope;
}

const TransferSelectSlotContent: React.FC<TransferSelectSlotProps> = ({ slotPreferenceScope }) => {
  const vm = useTransferSelectSlotVm({ slotPreferenceScope });
  const { handleContinue: vmHandleContinue } = vm;
  const { state, transferScheduleDispatch } = useTransferScheduleContext();
  const { timeSlots, timeWindows } = state;

  const [submissionErrorMessage, setSubmissionErrorMessage] = useState('');
  const [isLoadTimeSlotsLoading, setIsLoadTimeSlotsLoading] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const handleContinue = useCallback(() => {
    setIsContinuing(true);
    try {
      vmHandleContinue();
    } catch (err) {
      setIsContinuing(false);
      throw err;
    }
  }, [vmHandleContinue]);

  const handleLoadTimeSlots = useCallback(async () => {
    try {
      setIsLoadTimeSlotsLoading(true);
      await loadTimeSlots(transferScheduleDispatch);
    } catch (error: unknown) {
      window.location.reload();
    } finally {
      setIsLoadTimeSlotsLoading(false);
      setSubmissionErrorMessage('');
    }
  }, [transferScheduleDispatch]);

  const handleModalAction = () => {
    setSubmissionErrorMessage('');
    if (submissionErrorMessage === TIME_SLOT_SELECTION_ERROR) {
      void handleLoadTimeSlots();
    }
  };

  if (!vm.isInitialized || vm.vinEtchingLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner size={48} />
      </div>
    );
  }

  if (vm.hasError || !vm.hasSchedulingOptions) {
    return <SchedulingErrorPrompt />;
  }

  return (
    <>
      <div className={styles.container}>
        {vm.vinEtchingState === 'Applied' && <VinEtchingBanner />}
        <AddressDisplay />
        {(timeSlots.length > 0 || timeWindows.length > 0) && <TimeSlotPicker />}
      </div>
      <div className={styles.buttonContainer}>
        <div className={styles.buttonWrapper}>
          <Button
            isLoading={isLoadTimeSlotsLoading || isContinuing}
            data-analytics-id="pp_select_slot_continue"
            onClick={handleContinue}
            size="md"
            width="full"
            variant="primary"
            disabled={
              !vm.isTimeSlotSelected ||
              isLoadTimeSlotsLoading ||
              isContinuing ||
              isBlockingError(submissionErrorMessage)
            }
          >
            Continue
          </Button>
        </div>
      </div>

      <TimeSlotSelectionAlertModal
        isOpen={submissionErrorMessage === TIME_SLOT_SELECTION_ERROR}
        onButtonClick={handleModalAction}
      />
    </>
  );
};

export default function TransferSelectSlot({ slotPreferenceScope }: TransferSelectSlotProps = {}) {
  return (
    <TransferScheduleProvider>
      <OrderSummaryProvider>
        <ToastProvider>
          <ThemeProvider>
            <TransferSelectSlotContent slotPreferenceScope={slotPreferenceScope} />
          </ThemeProvider>
        </ToastProvider>
      </OrderSummaryProvider>
    </TransferScheduleProvider>
  );
}
